/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { createContext, FC, ReactNode, useContext, useEffect } from "react";
import { useLiveAPI, UseLiveAPIResults } from "../hooks/use-live-api";
import tools from "../ToolDeclaration";
import { Conversation, ToolCall } from "../multimodal-live-types";
import { useToolQueue } from "./ToolQueueContext";
import { getAllFormulas } from "../apis/n8n/processFormula";
import { getVectorData } from "../apis/n8n/get-vector-data";
import { calculateFormula } from "../apis/n8n/calculate-formula";
import { showDetailUrl } from "../apis/n8n/namespace";
import { showDetails } from "../apis/n8n/showDetails";

const LiveAPIContext = createContext<UseLiveAPIResults | undefined>(undefined);

export type LiveAPIProviderProps = {
  children: ReactNode;
  url?: string;
  apiKey: string;
};




export const LiveAPIProvider: FC<LiveAPIProviderProps> = ({
  url,
  apiKey,
  children,
}) => {
  const liveAPI = useLiveAPI({ url, apiKey });
  const client = liveAPI.client;
  const { conversation, setConversation, loadingTools, setLoadingTools } = useToolQueue();
  // configure new tool 

  useEffect(() => {
    liveAPI.setConfig({
      model: "models/gemini-2.0-flash-exp",
      generationConfig: {
        responseModalities: "audio",
        speechConfig: {
          voiceConfig: { prebuiltVoiceConfig: { voiceName: "Aoede" } },
        },
      },
      systemInstruction: {
        parts: [
          {
            // altair
            text: 'You are my helpful assistant. Any time I ask you for a graph call the "render_altair" function I have provided you. Dont ask for additional information just make your best judgement.',
          },
          {
            // formula name
            text: `You are an assistant with scientific formula detection capabilities. For each user query:

1. Automatically analyze if any of these are requested:
   - Mathematical formulas
   - Scientific terms
   - Technical frameworks
   - Methodology details

If detected, immediately call the get_formula() function with the identified terms. Use your best judgment without asking for clarification.

When possible, expand abbreviations and provide full names for technical terms from your knowledge base only if you are confident enought about the full form or details. while generating full form also include original formula that user have mentioned also.

After calling get_formula(), acknowledge with a natural, personalized message like:
"Processing Data about [ Specific formula] That...." or more personalized message you like.

Always combine the retrieved data with clear explanations in your response.
`,
          },
          // tool that manipulates the data in screen
          {
            text: `You are an assistant with scientific formula detection capabilities. For each user query:
              you have to analyze if user is looking to change the formulas shown. means if user query is intenting to change the formula changes shown or related query then user this toolCall get_formula_showcase() with the previous formulas shown in the response.
            `
          }
        ],
      },
      tools: [
        // there is a free-tier quota for search
        { googleSearch: {} },
        { functionDeclarations: Object.keys(tools).map((key) => tools[key as keyof typeof tools]) },
      ],
    });
  }, [liveAPI.setConfig]);

  // NOTE: tool consumers : analyze the tool call and use the formulas
  async function onFormulaToolCall(toolCall: ToolCall, conversation: any) {
    // get the formula
    const availableToolCollections = [tools.formulaTool.name, tools.altair.name, tools.calculateFormula.name, tools.mermaidVisualizationTool.name, tools.showDetails.name];
    const toolCalls = toolCall.functionCalls.find(
      (fc) => availableToolCollections.includes(fc.name),
    );

    console.log("toolCalls", toolCalls);

    switch (toolCalls?.name) {
      // get the formula and formula details
      case "get_formula":
        setConversation((prevConversation: any) => [
          ...prevConversation,
          { type: 'user', data: (toolCalls as any).args.prompt } as Conversation,
        ]);
        console.log("get_formula", toolCall);
        if (loadingTools.includes("formulas")) return;

        // add in history
        setLoadingTools([...loadingTools, "formulas"]);

        // get the data from vector base
        const vectorFormulaData = await getVectorData((toolCalls as any).args.formulaList.split(","));
        const formatedFormula = await getAllFormulas(vectorFormulaData.output.join("."), (toolCalls as any).args.prompt);

        client.sendToolResponse({
          functionResponses: toolCall.functionCalls.map((fc) => ({
            response: {
              data: vectorFormulaData.output.join('.'),
              formulas: formatedFormula ? formatedFormula.data : [],
              explainationGuide: "First Explain the data field and then explain the very breif and follow up for that.dont pick up formulas first."
            },
            id: fc.id,
          })),
        });
        setLoadingTools(loadingTools.filter((tool) => tool !== "formulas"));
        console.log(vectorFormulaData, "mmmmmmmmmmmmmmmmmmmm")
        setConversation((prevConversation: any) => [
          ...prevConversation,
          { type: 'formulas', data: formatedFormula ? formatedFormula.data : [], other: vectorFormulaData.highRanked.join(".") } as Conversation,
        ]);
        // const data = await getAll
        break;



      // tool that calculates the formula
      case "calculate_formula":
        console.log("calculate_formula", toolCall);
        setLoadingTools([...loadingTools, "calculate"]);
        setConversation((prevConversation: any) => [
          ...prevConversation,
          { type: 'user', data: (toolCalls as any).args.prompt },
        ]);
        const conversationMemory = JSON.stringify(conversation);
        console.log("Updated conversation:", conversation);
        console.log("Conversation memory:", conversationMemory);
        const response = await calculateFormula((toolCalls as any).args.prompt, (toolCalls as any).args.intent, conversationMemory);

        console.log(response)

        setConversation((prevConversation: any) => {
          const updatedConversation = [...prevConversation, { type: 'calculate', data: response.data }];
          console.log("Updated conversation after set:", updatedConversation, " - - - - ", prevConversation);
          return updatedConversation;
        });

        // stop loading
        setLoadingTools(loadingTools.filter((tool) => tool !== "calculate"));
        // send response to gemini
        client.sendToolResponse({
          functionResponses: toolCall.functionCalls.map((fc) => ({
            response: {
              data: response.data,
            },
            id: fc.id,
          })),
        });
        break;

      /** 
       *  tool that helps to process the details and show the details 
       */

      case "get_formula_showcase":
        const intent = (toolCalls as any).args.intent;
        const prompt = (toolCalls as any).args.prompt;
        // add to conversation
        setConversation((prevConversation: any) => [
          ...prevConversation,
          { type: 'user', data: prompt },
        ]);


        // add loading: 
        setLoadingTools([...loadingTools, "showcase"]);

        // get the data from api
        const showCaseConversationMemory = JSON.stringify(conversation);
        const resp = await showDetails(prompt, intent, showCaseConversationMemory);
        console.log("showcase response", resp);
        setConversation((prevConversation: any) => [
          ...prevConversation,
          { type: 'calculate', data: resp.data }
        ])
        // stop loading
        setLoadingTools(loadingTools.filter((tool) => tool !== "showcase"));

        // send response to gemini
        client.sendToolResponse({
          functionResponses: toolCall.functionCalls.map((fc) => ({
            response: {
              data: resp.data,
            },
            id: fc.id,
          })),
        });
        console.log("get_formula_showcase", toolCall);
        break;




      case "render_altair":
        const graph = (toolCalls as any).args.json_graph;
        const quickExplaination = (toolCalls as any).args.quickExplaination;

        setConversation((prevConversation: any) => [
          ...prevConversation,
          { type: 'user', data: (toolCalls as any).args.prompt },
          { type: 'graph', data: graph, other: [quickExplaination] }
        ]);


        // send response to gemini
        client.sendToolResponse({
          functionResponses: toolCall.functionCalls.map((fc) => ({
            response: {
              data: graph,
              explaination: "Here is your requested graph. " + quickExplaination
            },
            id: fc.id,
          })),
        })
        console.log("render_altair", toolCall);
        break;

      case "get_mermaid_visualization":
        const mermaidCode = (toolCalls as any).args.mermaidCode;
        setConversation((prevConversation: any) => [
          ...prevConversation,
          { type: 'user', data: (toolCalls as any).args.prompt },
          { type: 'visualization', data: mermaidCode }
        ]);

        // send response to gemini
        client.sendToolResponse({
          functionResponses: toolCall.functionCalls.map((fc) => ({
            response: {
              data: mermaidCode,
            },
            id: fc.id,
          })),
        })
        console.log("get_mermaid_visualization", toolCall);
        break;
      default:
        console.log("received unmatched message", toolCall);
    }
  }

  useEffect(() => {
    const handler = (toolCall: ToolCall) => onFormulaToolCall(toolCall, conversation);

    client.on("toolcall", handler);
    return () => {
      client.off("toolcall", handler);
    };
  }, [client, conversation]);


  useEffect(() => {
    console.log("conversation - - - - - ", conversation)
  }, [conversation])

  return (
    <LiveAPIContext.Provider value={liveAPI}>
      {children}
    </LiveAPIContext.Provider>
  );
};

export const useLiveAPIContext = () => {
  const context = useContext(LiveAPIContext);
  if (!context) {
    throw new Error("useLiveAPIContext must be used wihin a LiveAPIProvider");
  }
  return context;
};
